import {AxiosError} from "axios";
import {stringify} from "query-string";
import * as React from "react";
import {FormattedMessage} from "react-intl";
import {connect, ConnectedProps} from "react-redux";
import {Redirect, RouteComponentProps, withRouter} from "react-router-dom";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {actions} from "./actions";
import {User} from "./model";
import {selectors} from "./selectors";
import {oidcProviderInfo} from "../oidc/startOidcProvider";
import {userManager} from "../oidc/utils/userManager";
import {oidcUtils} from "../oidc/utils/OidcUtils";

interface WithAuthenticationStateProperties {
  user: User;
}

interface WithAuthenticationDispatchProperties {
  onUserFound: (user: User) => void;
}

type WithAuthenticationProperties = WithAuthenticationStateProperties & WithAuthenticationDispatchProperties & RouteComponentProps<{}>;

interface WithAuthenticationCheckState {
  authenticationCheckCompleted: boolean;
  authenticationCheckFailed: boolean;
  backendOffline: boolean;
}

const mapStateToProps = (state, ownProps) => {
  return {user: selectors.getUser(state), ...ownProps};
};

const mapDispatchToProps = (dispatch) => {
  return {
    onUserFound: (user) => dispatch(actions.setUser(user)),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);


type PropsFromRedux = ConnectedProps<typeof connector>;

export function WithAuthenticationCheck<WrappedComponentProps>(
    WrappedComponent: React.ComponentClass<WrappedComponentProps> | React.FunctionComponent<WrappedComponentProps>,
): React.ComponentClass<WrappedComponentProps & PropsFromRedux> {

  type WithAuthenticationCheckHOCProps = WithAuthenticationProperties & WithApiProperties & WrappedComponentProps & PropsFromRedux;

  class WithAuthenticationCheckHOC extends React.Component<WithAuthenticationCheckHOCProps, WithAuthenticationCheckState> {

    constructor(props) {
      super(props);
      this.state = {
        authenticationCheckCompleted: false,
        authenticationCheckFailed: false,
        backendOffline: false,
      };
    }

    componentDidMount() {
      const isOidcEnabled = oidcProviderInfo?.enabled;
      const sessionIsAvailable = oidcUtils.getOidcTokenValue() !== "";
      if (isOidcEnabled && sessionIsAvailable) {
        userManager.getUser()
            .then(() => {
              this.checkAuthenticated(this.props.user);
            })
            .catch((error: AxiosError) => {
              const authenticationCheckFailed = error?.response && error.response.status === 401;
              this.setState({
                authenticationCheckCompleted: true,
                authenticationCheckFailed,
                backendOffline: false,
              });
            })
      } else {
        this.checkAuthenticated(this.props.user);
      }
    }

    componentDidUpdate(prevProps) {
      if (prevProps.user !== this.props.user) {
        this.checkAuthenticated(this.props.user);
      }
    }

    checkAuthenticated = (user: User) => {
      const isOidc = oidcProviderInfo?.enabled;
      if (!user) { //if ControlRoom doesn't know who you are based on what's in redux
        const triggerRedirect = this.configureTriggerRedirect();
        //check if the backend knows who you are and store that result locally
        this.props.api.getCurrentUser().then((currentUser) => {
          if (currentUser === null) {
            if (isOidc && this.props.api.isOidcAuthenticated()) {
              // user was authenticated; however, check if the user has an admin or studiomanager role.
              const clientId = oidcProviderInfo?.clientId;
              if (this.props.api.isOidcAdminRole(clientId)) {
                this.updateCompletedState();
              } else {
                this.props.api.logoutOidcProvider();
              }
            } else {
              triggerRedirect();
            }
          } else if (isOidc && currentUser.username === "fusion_guest_user") {
            this.props.onUserFound(null);
          } else {
            this.props.onUserFound(currentUser);
            this.updateCompletedState();
          }
        }).catch(triggerRedirect);
      } else if (user.username === "fusion_guest_user") {
        // The Luciad guest user should not be allowed to login;however, force redirect to login page if you do.
        // I can reproduce if openId enabled is false and fusion security enabled is false.
        // If user is set to null, the render should go to login page
        this.props.onUserFound(null);
      } else {
        this.updateCompletedState();
      }
    }

    private updateCompletedState() {
      this.setState({
        authenticationCheckCompleted: true,
        backendOffline: false,
      });
    }

    private configureTriggerRedirect() {
      return (error?: AxiosError) => {
        //redirect to the login page if nobody knows
        const noResponse = error && !error.response && error.message.includes("Network");
        const badGateway = error?.response && error.response.status === 504; //when behind a proxy you get a bad gateway
        const authenticationCheckFailed = error?.response && error.response.status === 401;
        const backendOffline = noResponse || badGateway;

        this.setState({
          authenticationCheckCompleted: true,
          authenticationCheckFailed,
          backendOffline,
        });
      };
    }

    render() {
      const {authenticationCheckCompleted, backendOffline, authenticationCheckFailed} = this.state;
      const {user, location} = this.props;

      if (!authenticationCheckCompleted) {
        return <h1><FormattedMessage
            id="studio.authentication-check.header"
            defaultMessage="Authenticating..."
        /></h1>;
      }
      //authentication check completed...
      if (backendOffline) {
        return <Redirect to={{
          pathname: "/backendoffline",
          search: stringify({
            redirect: location.pathname + location.search,
          }),
        }}/>;
      }

      if (user === null || authenticationCheckFailed) {
        return <Redirect to={{
          pathname: "/login",
          search: stringify({
            redirect: location.pathname + location.search,
          }),
        }}/>;
      }

      return <WrappedComponent {...this.props as WrappedComponentProps} />;
    }
  }


  return withRouter(connector(WithApi(WithAuthenticationCheckHOC)));

}
